import {
  type ColumnDef,
  type ColumnVisibilityState,
  createColumnHelper,
  type RowSelectionState,
} from "@tanstack/react-table";
import { Building2, Copy, MoreHorizontal, UserX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { DataTable, type DataTableFeatures } from "~/components/data-table";
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import type { UserRow } from "~/features/admin/users/types";

interface UsersTableProps {
  rows: UserRow[];
  onSelect: (id: string) => void;
  onChangeOrganization: (user: UserRow) => void;
  emptyMessage: string;
  globalFilter: string;
  onGlobalFilterChange: (next: string) => void;
  columnVisibility: ColumnVisibilityState;
  onColumnVisibilityChange: (next: ColumnVisibilityState) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: (next: RowSelectionState) => void;
}

const helper = createColumnHelper<DataTableFeatures, UserRow>();

export function UsersTable({
  rows,
  onSelect,
  onChangeOrganization,
  emptyMessage,
  globalFilter,
  onGlobalFilterChange,
  columnVisibility,
  onColumnVisibilityChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  rowSelection,
  onRowSelectionChange,
}: UsersTableProps) {
  const { t } = useTranslation();

  const handleCopyId = async (id: string) => {
    try {
      await copyToClipboard(id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const columns: ColumnDef<DataTableFeatures, UserRow, any>[] = [
    helper.display({
      id: "select",
      enableSorting: false,
      enableGlobalFilter: false,
      header: (info) => {
        const filteredRows = info.table.getFilteredRowModel().rows;
        const allSelected = filteredRows.length > 0 && filteredRows.every((row) => row.getIsSelected());
        const someSelected = filteredRows.some((row) => row.getIsSelected());
        return (
          <Checkbox
            checked={allSelected ? true : someSelected ? "indeterminate" : false}
            onCheckedChange={(next) => {
              const selection: RowSelectionState = { ...rowSelection };
              for (const row of filteredRows) {
                if (next === true) {
                  selection[row.id] = true;
                } else {
                  delete selection[row.id];
                }
              }
              onRowSelectionChange(selection);
            }}
            aria-label={t("ui.selectAll")}
          />
        );
      },
      cell: (info) => (
        <Checkbox
          checked={info.row.getIsSelected()}
          onCheckedChange={() => info.row.toggleSelected()}
          onClick={(event) => event.stopPropagation()}
          aria-label={t("ui.select")}
        />
      ),
    }),
    helper.display({
      id: "deactivatedAt",
      header: () => "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => {
        if (!info.row.original.isDeactivated) return null;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <UserX className="h-4 w-4 text-warning" aria-label={t("ui.deactivated")} />
            </TooltipTrigger>
            <TooltipContent>{t("ui.deactivated")}</TooltipContent>
          </Tooltip>
        );
      },
    }),
    helper.accessor("displayName", {
      id: "displayName",
      header: () => t("model.user.displayName"),
      cell: (info) => <span className="font-medium text-fg-1">{info.getValue()}</span>,
    }),
    helper.accessor("email", {
      id: "email",
      header: () => t("model.user.email"),
      cell: (info) => <span className="text-fg-2">{info.getValue()}</span>,
    }),
    helper.accessor("organization.name", {
      id: "organization",
      header: () => t("model.user.organization"),
      enableGlobalFilter: false,
      cell: (info) => <span className="text-fg-2">{info.getValue()}</span>,
    }),
    // Segment and group are narrowed with the dedicated MultiSelect filters, so they stay
    // out of the text search rather than relying on array values missing the filter fn.
    helper.accessor("segments", {
      id: "segments",
      header: () => t("model.user.segments"),
      enableGlobalFilter: false,
      cell: (info) => <span className="text-fg-2">{info.getValue().join(" / ")}</span>,
    }),
    helper.accessor("groups", {
      id: "groups",
      header: () => t("model.user.groups"),
      enableGlobalFilter: false,
      cell: (info) => <span className="text-fg-2">{info.getValue().join(" / ")}</span>,
    }),
    helper.accessor("memo", {
      id: "memo",
      header: () => t("model.user.memo"),
      cell: (info) => <span className="whitespace-pre-line text-fg-2">{info.getValue()}</span>,
    }),
    helper.display({
      id: "actions",
      header: () => "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center justify-end gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={() => onSelect(row.id)}>
              {t("ui.view")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon" aria-label={t("ui.operations")}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onChangeOrganization(row)}>
                  <Building2 />
                  <span>{t("feature.users.organizationChange.title")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => handleCopyId(row.id)}>
                  <Copy />
                  <span>{t("ui.copyId")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    }),
  ];

  return (
    <DataTable<UserRow>
      columns={columns}
      data={rows}
      emptyMessage={emptyMessage}
      globalFilter={globalFilter}
      onGlobalFilterChange={onGlobalFilterChange}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={onColumnVisibilityChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      getRowId={(row) => row.id}
    />
  );
}
